import { Component } from '@theme/component';
import { ThemeEvents, CartErrorEvent, CartAddEvent } from '@theme/events';

/**
 * @typedef {Object} GiftCardRecipientFormRefs
 * @property {HTMLInputElement} giftCardCheckbox - Checkbox for toggling gift card recipient form
 * @property {HTMLDivElement} recipientFields - Container for recipient form fields
 * @property {HTMLInputElement} recipientName - Recipient name input field
 * @property {HTMLInputElement} recipientEmail - Recipient email input field
 * @property {HTMLInputElement} confirmRecipientEmail - Confirm recipient email input field
 * @property {HTMLTextAreaElement} recipientMessage - Gift message textarea
 * @property {HTMLInputElement} recipientSendOn - Send on date input
 * @property {HTMLInputElement} senderName - Sender name input field
 * @property {HTMLInputElement} [timezoneOffset] - Timezone offset hidden input (optional)
 * @property {HTMLInputElement} [controlFlag] - Shopify gift card control flag (optional as it's dynamically queried)
 * @property {HTMLDivElement} [nameError] - Name error message container (optional)
 * @property {HTMLDivElement} [emailError] - Email error message container (optional)
 * @property {HTMLDivElement} [confirmEmailError] - Confirm email error message container (optional)
 * @property {HTMLDivElement} [messageError] - Message error message container (optional)
 * @property {HTMLDivElement} [sendOnError] - Send on error message container (optional)
 * @property {HTMLDivElement} [senderNameError] - Sender name error message container (optional)
 * @property {HTMLSpanElement} [characterCount] - Character count display element (optional)
 * @property {HTMLDivElement} [liveRegion] - Live region for screen reader announcements (optional)
 * @property {HTMLDivElement} [datePickerWrapper] - Custom date picker wrapper element (optional)
 * @property {HTMLSpanElement} [datePickerPlaceholder] - Custom date picker placeholder element (optional)
 * @property {HTMLSpanElement} [datePickerDisplay] - Custom date picker display element for formatted date (optional)
 */

/**
 * @extends {Component<GiftCardRecipientFormRefs>}
 */
class GiftCardRecipientForm extends Component {
  static DeliveryMode = {
    SELF: 'self', // Send to my email
    RECIPIENT: 'recipient_form', // Send to recipient's email with form
  };

  /** Allowed characters in the gift message: A-Z, a-z, 0-9, whitespace, and ! ( ) - _ " ' " " ' ' , . ? */
  static #ALLOWED_MESSAGE_CHARS = /[^A-Za-z0-9\s!()\-_"'“”‘’,.?]/;

  #currentMode = GiftCardRecipientForm.DeliveryMode.SELF;

  // Store bound event handlers for cleanup
  /** @type {(() => void) | null} */
  #updateCharacterCountBound = null;
  /** @type {((event: Event) => void) | null} */
  #displayCartErrorBound = null;
  /** @type {(() => void) | null} */
  #cartAddEventBound = null;
  /** @type {((event: Event) => void) | null} */
  #formSubmitBound = null;
  /** @type {((event: Event) => void) | null} */
  #buttonClickBound = null;
  /** @type {(() => void) | null} */
  #validateEmailFieldBound = null;
  /** @type {(() => void) | null} */
  #validateEmailOnInputBound = null;
  /** @type {(() => void) | null} */
  #validateConfirmEmailFieldBound = null;
  /** @type {(() => void) | null} */
  #validateConfirmEmailOnInputBound = null;
  /** @type {(() => void) | null} */
  #validateSendOnDateBound = null;
  /** @type {boolean} */
  #isResettingDate = false;

  /**
   * Format date as YYYY-MM-DD
   * @param {Date} date
   * @returns {string}
   */
  static #formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  requiredRefs = [
    'giftCardCheckbox',
    'recipientFields',
    'recipientName',
    'recipientEmail',
    'confirmRecipientEmail',
    'recipientMessage',
    'recipientSendOn',
    'senderName',
  ];

  /**
   * Get all recipient input fields
   * @returns {(HTMLInputElement | HTMLTextAreaElement)[]} Array of input fields
   */
  get #inputFields() {
    return [
      this.refs.recipientName,
      this.refs.recipientEmail,
      this.refs.confirmRecipientEmail,
      this.refs.recipientMessage,
      this.refs.recipientSendOn,
      this.refs.senderName,
    ];
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initializeForm();

    // Check checkbox state after DOM is ready (handles browser back navigation)
    requestAnimationFrame(() => {
      if (this.refs.giftCardCheckbox.checked && this.#currentMode === GiftCardRecipientForm.DeliveryMode.SELF) {
        // Checkbox is checked but form is closed - sync the state
        this.#currentMode = GiftCardRecipientForm.DeliveryMode.RECIPIENT;
        this.#updateFormState();
      }
    });

    this.#updateCharacterCountBound = () => this.#updateCharacterCount();
    this.refs.recipientMessage.addEventListener('input', this.#updateCharacterCountBound);
    
    // Validate message on input for emojis and special characters
    this.refs.recipientMessage.addEventListener('input', () => this.#validateMessageField());

    // Add real-time email validation
    this.#validateEmailFieldBound = () => this.#validateEmailField();
    this.#validateEmailOnInputBound = () => this.#validateEmailOnInput();
    this.#validateConfirmEmailFieldBound = () => this.#validateConfirmEmailField();
    this.#validateConfirmEmailOnInputBound = () => this.#validateConfirmEmailOnInput();
    
    this.refs.recipientEmail.addEventListener('blur', this.#validateEmailFieldBound);
    this.refs.recipientEmail.addEventListener('input', this.#validateEmailOnInputBound);
    this.refs.confirmRecipientEmail.addEventListener('blur', this.#validateConfirmEmailFieldBound);
    this.refs.confirmRecipientEmail.addEventListener('input', this.#validateConfirmEmailOnInputBound);

    // Add date validation for iOS (iOS allows selecting past dates even with min attribute)
    this.#validateSendOnDateBound = () => this.#validateSendOnDate();
    this.refs.recipientSendOn.addEventListener('change', this.#validateSendOnDateBound);

    // Update placeholder visibility when date changes
    this.refs.recipientSendOn.addEventListener('change', () => this.#updateDatePickerPlaceholder());
    this.refs.recipientSendOn.addEventListener('input', () => this.#updateDatePickerPlaceholder());
    
    // Initialize placeholder visibility
    this.#updateDatePickerPlaceholder();

    this.#displayCartErrorBound = this.#displayCartError.bind(this);
    // @ts-ignore - #displayCartErrorBound is guaranteed to be non-null here
    document.addEventListener(ThemeEvents.cartError, this.#displayCartErrorBound);

    this.#cartAddEventBound = () => this.#handleCartAdd();
    document.addEventListener(ThemeEvents.cartUpdate, this.#cartAddEventBound);

    // Listen for form submit and button click to validate before submission and animation
    const form = this.closest('form');
    if (form) {
      const boundSubmitHandler = this.#validateBeforeSubmit.bind(this);
      this.#formSubmitBound = boundSubmitHandler;
      form.addEventListener('submit', boundSubmitHandler, { capture: true });

      // Also listen to button clicks to prevent animation if validation fails
      const addToCartButton = form.querySelector('button[type="submit"][name="add"]');
      if (addToCartButton) {
        const boundClickHandler = this.#validateBeforeClick.bind(this);
        this.#buttonClickBound = boundClickHandler;
        addToCartButton.addEventListener('click', boundClickHandler, { capture: true });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#updateCharacterCountBound) {
      this.refs.recipientMessage.removeEventListener('input', this.#updateCharacterCountBound);
      this.#updateCharacterCountBound = null;
    }

    // Remove email validation event listeners
    if (this.#validateEmailFieldBound) {
      this.refs.recipientEmail.removeEventListener('blur', this.#validateEmailFieldBound);
      this.#validateEmailFieldBound = null;
    }
    if (this.#validateEmailOnInputBound) {
      this.refs.recipientEmail.removeEventListener('input', this.#validateEmailOnInputBound);
      this.#validateEmailOnInputBound = null;
    }
    if (this.#validateConfirmEmailFieldBound) {
      this.refs.confirmRecipientEmail.removeEventListener('blur', this.#validateConfirmEmailFieldBound);
      this.#validateConfirmEmailFieldBound = null;
    }
    if (this.#validateConfirmEmailOnInputBound) {
      this.refs.confirmRecipientEmail.removeEventListener('input', this.#validateConfirmEmailOnInputBound);
      this.#validateConfirmEmailOnInputBound = null;
    }
    if (this.#validateSendOnDateBound) {
      this.refs.recipientSendOn.removeEventListener('change', this.#validateSendOnDateBound);
      this.#validateSendOnDateBound = null;
    }

    if (this.#displayCartErrorBound) {
      document.removeEventListener(ThemeEvents.cartError, this.#displayCartErrorBound);
      this.#displayCartErrorBound = null;
    }

    if (this.#cartAddEventBound) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.#cartAddEventBound);
      this.#cartAddEventBound = null;
    }

    if (this.#formSubmitBound) {
      const form = this.closest('form');
      if (form) {
        form.removeEventListener('submit', this.#formSubmitBound, { capture: true });
      }
      this.#formSubmitBound = null;
    }

    if (this.#buttonClickBound) {
      const form = this.closest('form');
      if (form) {
        const addToCartButton = form.querySelector('button[type="submit"][name="add"]');
        if (addToCartButton) {
          addToCartButton.removeEventListener('click', this.#buttonClickBound, { capture: true });
        }
      }
      this.#buttonClickBound = null;
    }
  }

  /**
   * Initialize form with default state, self delivery is selected by default
   */
  #initializeForm() {
    // Check if checkbox is already checked (e.g., from browser back navigation)
    const isChecked = this.refs.giftCardCheckbox.checked;
    
    if (isChecked) {
      // If checkbox is checked, open the form
      this.#currentMode = GiftCardRecipientForm.DeliveryMode.RECIPIENT;
      this.#updateFormState();
    } else {
      // Default state: unchecked and form hidden
      this.refs.giftCardCheckbox.checked = false;
      this.refs.recipientFields.hidden = true;
      this.#clearRecipientFields();
      this.#disableRecipientFields();
    }
    
    this.#setDateConstraints();
  }

  /**
   * Handle toggle between my email and recipient email
   * @param {string} mode - Delivery mode (either 'self' or 'recipient_form')
   * @param {Event} _event - Change event (unused)
   */
  toggleRecipientForm(mode, _event) {
    // Validate mode
    if (!Object.values(GiftCardRecipientForm.DeliveryMode).includes(mode)) {
      throw new Error(
        `Invalid delivery mode: ${mode}. Must be one of: ${Object.values(GiftCardRecipientForm.DeliveryMode).join(
          ', '
        )}`
      );
    }

    if (this.#currentMode === mode) return;
    this.#currentMode = mode;

    this.#updateFormState();
  }

  /**
   * Update form state based on current mode
   */
  #updateFormState() {
    const { DeliveryMode } = GiftCardRecipientForm;
    const isRecipientMode = this.#currentMode === DeliveryMode.RECIPIENT;

    this.refs.giftCardCheckbox.checked = isRecipientMode;

    this.refs.recipientFields.hidden = !isRecipientMode;

    if (isRecipientMode) {
      this.#enableRecipientFields();

      this.#updateCharacterCount();

      // Announce to screen readers
      if (this.refs.liveRegion) {
        this.refs.liveRegion.textContent =
          Theme.translations?.recipient_form_fields_visible || 'Recipient form fields are now visible';
      }

      // Focus first field for accessibility
      this.refs.recipientEmail.focus();
    } else {
      this.#clearRecipientFields();
      this.#disableRecipientFields();

      // Announce to screen readers
      if (this.refs.liveRegion) {
        this.refs.liveRegion.textContent =
          Theme.translations?.recipient_form_fields_hidden || 'Recipient form fields are now hidden';
      }
    }

    this.dispatchEvent(
      new CustomEvent('recipient:toggle', {
        detail: {
          mode: this.#currentMode,
          recipientFormVisible: isRecipientMode,
        },
        bubbles: true,
      })
    );
  }

  /**
   * Handle checkbox change event
   * @param {Event} event - Change event from checkbox
   */
  handleCheckboxChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    
    const isChecked = target.checked;
    const mode = isChecked
      ? GiftCardRecipientForm.DeliveryMode.RECIPIENT
      : GiftCardRecipientForm.DeliveryMode.SELF;
    this.toggleRecipientForm(mode, event);
  }

  /**
   * Clear all recipient form fields
   */
  #clearRecipientFields() {
    for (const field of this.#inputFields) {
      field.value = '';
    }

    this.#updateCharacterCount();
    this.#clearErrorMessages();
    this.#updateDatePickerPlaceholder();
  }

  /**
   * Disable recipient form fields when sending to self
   */
  #disableRecipientFields() {
    for (const field of this.#inputFields) {
      field.disabled = true;
      field.removeAttribute('required');
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
    }

    // Remove control field when sending to self
    const controlFlag = this.querySelector('input[name="properties[__shopify_send_gift_card_to_recipient]"]');
    if (controlFlag) {
      controlFlag.remove();
    }

    if (this.refs.timezoneOffset) {
      this.refs.timezoneOffset.disabled = true;
      this.refs.timezoneOffset.value = '';
    }

    this.#clearErrorMessages();
  }

  /**
   * Enable recipient form fields when sending to recipient
   */
  #enableRecipientFields() {
    for (const field of this.#inputFields) {
      field.disabled = false;
      if (field === this.refs.recipientEmail || field === this.refs.confirmRecipientEmail) {
        field.setAttribute('required', 'required');
      }
    }

    // Add control field when sending to recipient
    let controlFlag = this.querySelector('input[name="properties[__shopify_send_gift_card_to_recipient]"]');
    if (!controlFlag) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[__shopify_send_gift_card_to_recipient]';
      input.value = 'on';
      this.appendChild(input);
    }

    // Enable and set timezone offset
    if (this.refs.timezoneOffset) {
      this.refs.timezoneOffset.disabled = false;
      this.refs.timezoneOffset.value = new Date().getTimezoneOffset().toString();
    }

    // Set date constraints when enabling fields
    this.#setDateConstraints();
  }

  /**
   * Update character count display
   */
  #updateCharacterCount() {
    if (!this.refs.characterCount) return;

    const currentLength = this.refs.recipientMessage.value.length;
    const maxLength = this.refs.recipientMessage.maxLength;

    const template = this.refs.characterCount.getAttribute('data-template');
    if (!template) return;

    const updatedText = template.replace('[current]', currentLength.toString()).replace('[max]', maxLength.toString());

    this.refs.characterCount.textContent = updatedText;

    if (currentLength >= maxLength) {
      this.refs.recipientMessage.classList.add('recipient-message--limit-reached');
    } else {
      this.refs.recipientMessage.classList.remove('recipient-message--limit-reached');
    }
  }

  /**
   * Set date constraints for the send on date picker
   * Prevents selecting past dates and limits to 90 days in the future
   */
  #setDateConstraints() {
    const today = new Date();
    const maxDate = new Date();
    maxDate.setDate(today.getDate() + 90);

    // TODO: Uncomment after testing past date validation on Chrome
    this.refs.recipientSendOn.setAttribute('min', GiftCardRecipientForm.#formatDate(today));
    this.refs.recipientSendOn.setAttribute('max', GiftCardRecipientForm.#formatDate(maxDate));
  }

  /**
   * Format date from YYYY-MM-DD to dd/mm/yyyy
   * @param {string} dateString - Date in YYYY-MM-DD format
   * @returns {string} - Date in dd/mm/yyyy format
   */
  static #formatDisplayDate(dateString) {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  }

  /**
   * Update date picker placeholder and display visibility
   * Shows placeholder when no date is selected, shows formatted date when selected
   */
  #updateDatePickerPlaceholder() {
    const dateValue = this.refs.recipientSendOn.value;
    const hasValue = dateValue !== '';
    
    if (this.refs.datePickerWrapper) {
      if (hasValue) {
        this.refs.datePickerWrapper.classList.add('has-value');
      } else {
        this.refs.datePickerWrapper.classList.remove('has-value');
      }
    }

    // Update custom date display with formatted date
    if (this.refs.datePickerDisplay) {
      this.refs.datePickerDisplay.textContent = hasValue 
        ? GiftCardRecipientForm.#formatDisplayDate(dateValue) 
        : '';
    }
  }

  /**
   * Handles cart error events
   * @param {CartErrorEvent} event - The cart error event
   */
  #displayCartError(event) {
    if (event.detail?.data) {
      const data = event.detail.data;
      // @ts-ignore - data structure may vary
      const message = data.message;
      // @ts-ignore - data structure may vary
      const errors = data.errors;
      // @ts-ignore - data structure may vary
      const description = data.description;

      // Display the error message
      if (errors && typeof errors === 'object') {
        this.#displayErrorMessage(message || 'There was an error', errors);
      } else if (message) {
        this.#displayErrorMessage(message, description || '');
      }
    }
  }

  /**
   * Display error messages in the appropriate error containers
   * @param {string} title - The main error message title
   * @param {Object} body - Error details
   */
  #displayErrorMessage(title, body) {
    this.#clearErrorMessages();

    if (typeof body === 'object' && body !== null) {
      /** @type {Record<string, {inputRef: string, errorRef: string}>} */
      const fieldMap = {
        name: { inputRef: 'recipientName', errorRef: 'nameError' },
        email: { inputRef: 'recipientEmail', errorRef: 'emailError' },
        confirm_email: { inputRef: 'confirmRecipientEmail', errorRef: 'confirmEmailError' },
        message: { inputRef: 'recipientMessage', errorRef: 'messageError' },
        send_on: { inputRef: 'recipientSendOn', errorRef: 'sendOnError' },
        sender_name: { inputRef: 'senderName', errorRef: 'senderNameError' },
      };

      for (const [field, errorMessages] of Object.entries(body)) {
        const fieldConfig = fieldMap[field];
        if (!fieldConfig) continue;

        const { inputRef, errorRef } = fieldConfig;
        const errorContainer = this.refs[errorRef];
        const inputElement = this.refs[inputRef];

        if (errorContainer && errorContainer instanceof HTMLElement) {
          const errorTextElement = errorContainer.querySelector('span');
          if (errorTextElement) {
            const message = Array.isArray(errorMessages) ? errorMessages.join(', ') : errorMessages;
            errorTextElement.textContent = `${message}.`;
          }

          errorContainer.classList.remove('hidden');
        }

        if (inputElement && inputElement instanceof HTMLElement) {
          // Set ARIA attributes for accessibility
          inputElement.setAttribute('aria-invalid', 'true');
          const errorId = `RecipientForm-${field}-error-${this.dataset.sectionId || 'default'}`;
          inputElement.setAttribute('aria-describedby', errorId);
        }
      }
    }

    // Announce errors to screen readers
    if (this.refs.liveRegion) {
      this.refs.liveRegion.textContent =
        title || Theme.translations?.recipient_form_error || 'There was an error with the form submission';
    }
  }

  /**
   * Clear all error messages and reset ARIA attributes
   */
  #clearErrorMessages() {
    // List of error container refs
    const errorRefs = ['nameError', 'emailError', 'confirmEmailError', 'messageError', 'sendOnError', 'senderNameError'];

    for (const errorRef of errorRefs) {
      const errorContainer = this.refs[errorRef];
      if (errorContainer && errorContainer instanceof HTMLElement) {
        errorContainer.classList.add('hidden');
        const errorTextElement = errorContainer.querySelector('span');
        if (errorTextElement) {
          errorTextElement.textContent = '';
        }
      }
    }

    // Remove ARIA attributes from all input fields
    for (const field of this.#inputFields) {
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
    }

    // Clear live region announcement
    if (this.refs.liveRegion) {
      this.refs.liveRegion.textContent = '';
    }
  }

  /**
   * @param {boolean} invalid - Result of message emoji/special-char check (caller runs validation once).
   */
  #syncMessageHintHighlight(invalid) {
    const hint = this.refs.messageHint;
    if (!hint || !(hint instanceof HTMLElement)) return;
    hint.classList.toggle('recipient-form__hint--error', invalid);
  }

  #handleCartAdd() {
    this.#clearErrorMessages();
  }

  /**
   * Validate form before button click (prevents animation)
   * @param {MouseEvent} event - Button click event
   */
  #validateBeforeClick(event) {
    // Only validate if the gift card checkbox is checked
    if (!this.refs.giftCardCheckbox.checked) {
      // Clear any validation state when checkbox is unchecked
      const form = this.closest('form');
      if (form) {
        form.removeAttribute('data-gift-card-validation-failed');
      }
      return; // Allow click if checkbox is not checked
    }

    // Perform validation
    if (!this.#performValidation()) {
      // Validation failed - set data attribute on form to signal validation failure
      const form = this.closest('form');
      if (form) {
        form.setAttribute('data-gift-card-validation-failed', 'true');
      }
      
      // Prevent click event from propagating
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    }

    // Validation passed - clear validation state and allow click to proceed
    const form = this.closest('form');
    if (form) {
      form.removeAttribute('data-gift-card-validation-failed');
    }
    return true;
  }

  /**
   * Validate form before submission
   * @param {SubmitEvent} event - Form submit event
   */
  #validateBeforeSubmit(event) {
    // Only validate if the gift card checkbox is checked
    if (!this.refs.giftCardCheckbox.checked) {
      return; // Allow submission if checkbox is not checked
    }

    // Perform validation
    if (!this.#performValidation()) {
      // Validation failed - prevent submission
      event.preventDefault();
      event.stopPropagation();
      return false;
    }

    // Sanitize the message field before submission
    const messageField = this.refs.recipientMessage;
    if (messageField && messageField.value) {
      messageField.value = this.#sanitizeMessage(messageField.value);
    }

    // Validation passed, allow submission
    return true;
  }

  /**
   * Perform validation and display errors if any
   * @returns {boolean} True if validation passes, false otherwise
   */
  #performValidation() {
    // Clear previous errors
    this.#clearErrorMessages();

    const errors = {};
    let hasErrors = false;

    // Validate recipient email
    const recipientEmail = this.refs.recipientEmail.value.trim();
    if (!recipientEmail) {
      errors.email = Theme.translations?.recipient_form_email_required || 'Recipient email is required';
      hasErrors = true;
    } else if (!this.#isValidEmail(recipientEmail)) {
      errors.email = Theme.translations?.recipient_form_email_invalid || 'Please enter a valid email address';
      hasErrors = true;
    }

    // Validate confirm recipient email
    const confirmEmail = this.refs.confirmRecipientEmail.value.trim();
    if (!confirmEmail) {
      errors.confirm_email = Theme.translations?.recipient_form_confirm_email_required || 'Please confirm the recipient email';
      hasErrors = true;
    } else if (recipientEmail && confirmEmail !== recipientEmail) {
      errors.confirm_email = Theme.translations?.recipient_form_emails_not_match || 'Email addresses do not match';
      hasErrors = true;
    }

    // Validate message for emojis and special characters
    const message = this.refs.recipientMessage.value;
    if (message && this.#containsEmojiOrSpecialChars(message)) {
      // Set aria-invalid for red border but don't add to errors object (no error message)
      this.refs.recipientMessage.setAttribute('aria-invalid', 'true');
      errors.message = '';
      hasErrors = true;
    } else if (message) {
      // Clear aria-invalid if message is valid
      this.refs.recipientMessage.removeAttribute('aria-invalid');
    } else {
      // Clear aria-invalid if message is empty
      this.refs.recipientMessage.removeAttribute('aria-invalid');
    }

    // If there are errors, display them
    if (hasErrors) {
      this.#displayErrorMessage('', errors);

      // Set data attribute on form to signal validation failure
      const form = this.closest('form');
      if (form) {
        form.setAttribute('data-gift-card-validation-failed', 'true');
      }

      // Focus on first error field
      if (errors.email && this.refs.recipientEmail) {
        this.refs.recipientEmail.focus();
      } else if (errors.confirm_email && this.refs.confirmRecipientEmail) {
        this.refs.confirmRecipientEmail.focus();
      } else if ('message' in errors && this.refs.recipientMessage) {
        this.refs.recipientMessage.focus();
      }

      // Announce error to screen readers
      if (this.refs.liveRegion) {
        this.refs.liveRegion.textContent =
          Theme.translations?.recipient_form_validation_error || 'Please fix the errors in the form';
      }

      return false;
    }

    // Validation passed - clear validation state
    const form = this.closest('form');
    if (form) {
      form.removeAttribute('data-gift-card-validation-failed');
    }
    return true;
  }

  /**
   * Validate email format
   * @param {string} email - Email address to validate
   * @returns {boolean} True if email is valid
   */
  #isValidEmail(email) {
    const emailRegex = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }

  /**
   * Validate message field on input (shows red border but no error message)
   */
  #validateMessageField() {
    const message = this.refs.recipientMessage.value;
    const invalid = Boolean(message && this.#containsEmojiOrSpecialChars(message));
    if (invalid) {
      this.refs.recipientMessage.setAttribute('aria-invalid', 'true');
    } else {
      this.refs.recipientMessage.removeAttribute('aria-invalid');
    }
    this.#syncMessageHintHighlight(invalid);
  }

  /**
   * Validate recipient email field on blur
   */
  #validateEmailField() {
    const email = this.refs.recipientEmail.value.trim();
    const errorContainer = this.refs.emailError;
    
    // Clear previous error
    if (errorContainer && errorContainer instanceof HTMLElement) {
      errorContainer.classList.add('hidden');
      const errorTextElement = errorContainer.querySelector('span');
      if (errorTextElement) {
        errorTextElement.textContent = '';
      }
    }
    this.refs.recipientEmail.removeAttribute('aria-invalid');
    this.refs.recipientEmail.removeAttribute('aria-describedby');

    // Validate if field has value
    if (email) {
      if (!this.#isValidEmail(email)) {
        this.#showEmailError('email', Theme.translations?.recipient_form_email_invalid || 'Please enter a valid email address');
      } else {
        // If email is valid, check if confirm email matches
        this.#validateConfirmEmailField();
      }
    }
  }

  /**
   * Validate recipient email on input (clear error if valid)
   */
  #validateEmailOnInput() {
    const email = this.refs.recipientEmail.value.trim();
    
    // If email becomes valid, clear error
    if (email && this.#isValidEmail(email)) {
      const errorContainer = this.refs.emailError;
      if (errorContainer && errorContainer instanceof HTMLElement) {
        const errorTextElement = errorContainer.querySelector('span');
        if (errorTextElement && errorTextElement.textContent.includes('valid email')) {
          errorContainer.classList.add('hidden');
          errorTextElement.textContent = '';
          this.refs.recipientEmail.removeAttribute('aria-invalid');
          this.refs.recipientEmail.removeAttribute('aria-describedby');
        }
      }
      
      // Check if confirm email matches
      this.#validateConfirmEmailOnInput();
    }
  }

  /**
   * Validate confirm email field on blur
   */
  #validateConfirmEmailField() {
    const recipientEmail = this.refs.recipientEmail.value.trim();
    const confirmEmail = this.refs.confirmRecipientEmail.value.trim();
    const errorContainer = this.refs.confirmEmailError;
    
    // Clear previous error
    if (errorContainer && errorContainer instanceof HTMLElement) {
      errorContainer.classList.add('hidden');
      const errorTextElement = errorContainer.querySelector('span');
      if (errorTextElement) {
        errorTextElement.textContent = '';
      }
    }
    this.refs.confirmRecipientEmail.removeAttribute('aria-invalid');
    this.refs.confirmRecipientEmail.removeAttribute('aria-describedby');

    // Validate if field has value
    if (confirmEmail) {
      if (!this.#isValidEmail(confirmEmail)) {
        this.#showEmailError('confirm_email', Theme.translations?.recipient_form_email_invalid || 'Please enter a valid email address');
      } else if (recipientEmail && confirmEmail !== recipientEmail) {
        this.#showEmailError('confirm_email', Theme.translations?.recipient_form_emails_not_match || 'Email addresses do not match');
      }
    } else if (recipientEmail) {
      // If recipient email exists but confirm is empty, show required error
      this.#showEmailError('confirm_email', Theme.translations?.recipient_form_confirm_email_required || 'Please confirm the recipient email');
    }
  }

  /**
   * Validate confirm email on input (check matching)
   */
  #validateConfirmEmailOnInput() {
    const recipientEmail = this.refs.recipientEmail.value.trim();
    const confirmEmail = this.refs.confirmRecipientEmail.value.trim();
    
    // Only validate matching if both fields have values
    if (recipientEmail && confirmEmail) {
      if (this.#isValidEmail(recipientEmail) && this.#isValidEmail(confirmEmail)) {
        if (recipientEmail === confirmEmail) {
          // Emails match - clear error
          const errorContainer = this.refs.confirmEmailError;
          if (errorContainer && errorContainer instanceof HTMLElement) {
            const errorTextElement = errorContainer.querySelector('span');
            if (errorTextElement && errorTextElement.textContent.includes('do not match')) {
              errorContainer.classList.add('hidden');
              errorTextElement.textContent = '';
              this.refs.confirmRecipientEmail.removeAttribute('aria-invalid');
              this.refs.confirmRecipientEmail.removeAttribute('aria-describedby');
            }
          }
        } else {
          // Emails don't match - show error
          this.#showEmailError('confirm_email', Theme.translations?.recipient_form_emails_not_match || 'Email addresses do not match');
        }
      }
    }
  }

  /**
   * Validate send on date field (iOS workaround - iOS allows selecting past dates even with min attribute)
   */
  #validateSendOnDate() {
    // Skip validation if we're programmatically resetting the date
    if (this.#isResettingDate) return;

    const dateValue = this.refs.recipientSendOn.value;
    if (!dateValue) {
      // Clear error when user clears the date
      this.#clearFieldError('send_on');
      return;
    }

    const selectedDate = new Date(dateValue);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Check if selected date is in the past
    if (selectedDate < today) {
      // Set flag to prevent recursive validation
      this.#isResettingDate = true;
      this.refs.recipientSendOn.value = GiftCardRecipientForm.#formatDate(today);
      this.#isResettingDate = false;

      // Show error message
      this.#showFieldError(
        'send_on',
        Theme.translations?.recipient_form_past_date_error || ''
      );

      // Announce to screen readers
      if (this.refs.liveRegion) {
        this.refs.liveRegion.textContent =
          Theme.translations?.recipient_form_past_date_error || '';
      }
    } else {
      // Clear any existing error
      this.#clearFieldError('send_on');
    }
  }

  /**
   * Show field error message
   * @param {string} field - Field name
   * @param {string} message - Error message to display
   */
  #showFieldError(field, message) {
    /** @type {Record<string, {inputRef: string, errorRef: string}>} */
    const fieldMap = {
      email: { inputRef: 'recipientEmail', errorRef: 'emailError' },
      confirm_email: { inputRef: 'confirmRecipientEmail', errorRef: 'confirmEmailError' },
      send_on: { inputRef: 'recipientSendOn', errorRef: 'sendOnError' },
    };

    const fieldConfig = fieldMap[field];
    if (!fieldConfig) return;

    const { inputRef, errorRef } = fieldConfig;
    const errorContainer = this.refs[errorRef];
    const inputElement = this.refs[inputRef];

    if (errorContainer && errorContainer instanceof HTMLElement) {
      const errorTextElement = errorContainer.querySelector('span');
      if (errorTextElement) {
        errorTextElement.textContent = `${message}.`;
      }
      errorContainer.classList.remove('hidden');
    }

    if (inputElement && inputElement instanceof HTMLElement) {
      inputElement.setAttribute('aria-invalid', 'true');
      const errorId = `RecipientForm-${field}-error-${this.dataset.sectionId || 'default'}`;
      inputElement.setAttribute('aria-describedby', errorId);
    }
  }

  /**
   * Clear field error message
   * @param {string} field - Field name
   */
  #clearFieldError(field) {
    /** @type {Record<string, {inputRef: string, errorRef: string}>} */
    const fieldMap = {
      email: { inputRef: 'recipientEmail', errorRef: 'emailError' },
      confirm_email: { inputRef: 'confirmRecipientEmail', errorRef: 'confirmEmailError' },
      send_on: { inputRef: 'recipientSendOn', errorRef: 'sendOnError' },
    };

    const fieldConfig = fieldMap[field];
    if (!fieldConfig) return;

    const { inputRef, errorRef } = fieldConfig;
    const errorContainer = this.refs[errorRef];
    const inputElement = this.refs[inputRef];

    if (errorContainer && errorContainer instanceof HTMLElement) {
      errorContainer.classList.add('hidden');
      const errorTextElement = errorContainer.querySelector('span');
      if (errorTextElement) {
        errorTextElement.textContent = '';
      }
    }

    if (inputElement && inputElement instanceof HTMLElement) {
      inputElement.removeAttribute('aria-invalid');
      inputElement.removeAttribute('aria-describedby');
    }
  }

  /**
   * Show email error message
   * @param {string} field - Field name ('email' or 'confirm_email')
   * @param {string} message - Error message to display
   */
  #showEmailError(field, message) {
    /** @type {Record<string, {inputRef: string, errorRef: string}>} */
    const fieldMap = {
      email: { inputRef: 'recipientEmail', errorRef: 'emailError' },
      confirm_email: { inputRef: 'confirmRecipientEmail', errorRef: 'confirmEmailError' },
    };

    const fieldConfig = fieldMap[field];
    if (!fieldConfig) return;

    const { inputRef, errorRef } = fieldConfig;
    const errorContainer = this.refs[errorRef];
    const inputElement = this.refs[inputRef];

    if (errorContainer && errorContainer instanceof HTMLElement) {
      const errorTextElement = errorContainer.querySelector('span');
      if (errorTextElement) {
        errorTextElement.textContent = `${message}.`;
      }
      errorContainer.classList.remove('hidden');
    }

    if (inputElement && inputElement instanceof HTMLElement) {
      inputElement.setAttribute('aria-invalid', 'true');
      const errorId = `RecipientForm-${field}-error-${this.dataset.sectionId || 'default'}`;
      inputElement.setAttribute('aria-describedby', errorId);
    }
  }

  /**
   * Check if text contains characters outside the allowed set.
   * Allowed: A-Z, a-z, 0-9, whitespace, and ! ( ) - _ " ' " " ' ' , . ?
   * @param {string} text - Text to check
   * @returns {boolean} True if text contains disallowed characters
   */
  #containsEmojiOrSpecialChars(text) {
    return GiftCardRecipientForm.#ALLOWED_MESSAGE_CHARS.test(text);
  }

  /**
   * Sanitize message by stripping any characters outside the allowed set.
   * Allowed: A-Z, a-z, 0-9, whitespace, and ! ( ) - _ " ' " " ' ' , . ?
   * @param {string} text - Text to sanitize
   * @returns {string} Sanitized text
   */
  #sanitizeMessage(text) {
    return text.replace(new RegExp(GiftCardRecipientForm.#ALLOWED_MESSAGE_CHARS.source, 'g'), '');
  }
}

// Register the custom element
customElements.define('gift-card-recipient-form', GiftCardRecipientForm);
